import { useRegisterSocketClient } from '@talos/kyoko';
import { useEffect, useState } from 'react';
import { CoinMetricsWebSocketClient } from './CoinMetricsWebSocketClient';
import { CM_WEBSOCKET_CLIENT_KEY, COIN_METRICS_PRO_WS_BASE_URL } from './coinMetricsConstants';
import { useCoinMetricsApiKey } from './coinMetricsCredentialsSlice';

/**
 * Constructs a `CoinMetricsWebSocketClient` once an API key is available (recreating it whenever the key
 * changes, mirroring how `WebSocketClientProvider` recreates the primary Talos client on auth changes),
 * and registers it under `CM_WEBSOCKET_CLIENT_KEY` so `useSubscription(request, { client: 'cm' })` can
 * find it from anywhere in the tree.
 */
export function useCoinMetricsSocketClient(): void {
  const apiKey = useCoinMetricsApiKey();
  const [client, setClient] = useState<CoinMetricsWebSocketClient | null>(null);

  useEffect(() => {
    if (!apiKey) {
      setClient(null);
      return;
    }
    const nextClient = new CoinMetricsWebSocketClient({ apiKey });
    nextClient.connect(COIN_METRICS_PRO_WS_BASE_URL);
    setClient(nextClient);
    return () => {
      nextClient.close();
    };
  }, [apiKey]);

  useRegisterSocketClient(CM_WEBSOCKET_CLIENT_KEY, client);
}

/**
 * Renders nothing — exists only so `useCoinMetricsSocketClient` can be mounted alongside
 * `CoinMetricsCredentialsSyncer` in `ContextProvider.tsx`, where both Redux (for the API key) and the
 * nearest `WebSocketClientProvider` (mounted higher in the tree — see `App.tsx`) are reachable.
 */
export function CoinMetricsSocketClientRegistrar() {
  useCoinMetricsSocketClient();
  return null;
}
